import type { QueryPlan } from '@aibi/analysis-engine';
import type { AnalysisState, QueryResult } from '@aibi/contracts';
import type { AppError } from '@aibi/shared';
import { useEffect, useState } from 'react';
import { useServices } from '../../app/services';

export type QueryResultState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'success'; readonly result: QueryResult }
  | { readonly status: 'error'; readonly error: AppError };

type Settled =
  | { readonly key: string; readonly ok: true; readonly result: QueryResult }
  | { readonly key: string; readonly ok: false; readonly error: AppError };

/**
 * Obtiene el `QueryResult` de un plan a traves del puerto `QueryResultSource`. La clave es
 * la huella del plan (excluye la presentacion): cambiar de tabla a barras no recarga.
 * Las cargas obsoletas se cancelan con `AbortController`. El Analysis State acompana al
 * plan para la ejecucion real (el servidor re-planifica a partir de el).
 */
export function useQueryResult(
  plan: QueryPlan | null,
  analysisState?: AnalysisState,
): QueryResultState {
  const { queryResultSource } = useServices();
  const [settled, setSettled] = useState<Settled | null>(null);
  const key = plan === null ? null : `${plan.analysisStateId}:${plan.fingerprint}`;

  useEffect(() => {
    if (plan === null || key === null) return;
    const controller = new AbortController();
    const options = analysisState
      ? { signal: controller.signal, analysisState }
      : { signal: controller.signal };
    void queryResultSource.load(plan, options).then((outcome) => {
      if (controller.signal.aborted) return;
      setSettled(
        outcome.ok
          ? { key, ok: true, result: outcome.value }
          : { key, ok: false, error: outcome.error },
      );
    });
    return () => {
      controller.abort();
    };
    // El plan se identifica por su huella: un plan nuevo con la misma huella no recarga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, queryResultSource]);

  if (key === null) return { status: 'idle' };
  if (settled?.key !== key) return { status: 'loading' };
  return settled.ok
    ? { status: 'success', result: settled.result }
    : { status: 'error', error: settled.error };
}
